/**
 * Input/response contracts for the `removeFromEvent` callable (ADR-089).
 *
 * Unified callable that handles both former operations:
 *   - Revoking a pending invitation (no membership yet, contact case)
 *   - Removing an active member (membership exists, cascade-delete data)
 *
 * Per ADR-089, "revoke" and "remove" are the same operation: the person is
 * no longer in the event. The invitation status flips to `'revoked'` as
 * audit, and any active membership + event-scoped data is cascade-deleted.
 *
 * Caller passes whichever identifier the UI has on hand:
 *   - `personId` — for roster rows / eventParticipants rows
 *   - `userId` — legacy Auth UID input for older callers
 *   - `invitationId` — for pending invitation rows
 *
 * At least one is required. The function cross-resolves: a person/member
 * lookup finds the corresponding invitation by email and revokes it; an
 * invitation lookup finds any matching membership and cascade-deletes it.
 */

import { z } from 'zod';

export const removeFromEventInputSchema = z
  .object({
    eventId: z.string().min(1),
    personId: z.string().min(1).optional(),
    userId: z.string().min(1).optional(),
    invitationId: z.string().min(1).optional(),
  })
  .strict()
  .refine((data) => Boolean(data.personId || data.userId || data.invitationId), {
    message: 'Either personId, userId, or invitationId is required.',
  });

export type RemoveFromEventInput = z.infer<typeof removeFromEventInputSchema>;

export interface RemoveFromEventResponse {
  success: true;
  /** Number of event-scoped docs cascade-deleted (notes, receipts, etc.). 0 if no membership existed. */
  descendantsDeleted: number;
  /** True if neither a membership nor a non-revoked invitation existed (idempotent no-op). */
  alreadyRemoved: boolean;
}
